import type { PosDesenlace } from "@/platform/types";
import type { InvoiceEmissionResult } from "@/utils/fiscalTicket";
import { codigoDelError, estadoDelError } from "./errores";
import { textoDeLaObservacion } from "./observaciones";

/**
 * Qué hace la cola con cada respuesta del lote (trd-client#338 AC-B; la tabla
 * de §2.2 de `OFFLINE-POS-RONDA-1.md`). Decide por el código, nunca por el
 * status solo. Rust aplica el desenlace (`src-tauri/src/pos/queue.rs`).
 */

/** `SaleResult` (§2.2): uno por venta, en el mismo orden del lote. */
export interface SaleResult {
  saleId: string;
  status: string;
  orderId?: string;
  /** El código de retiro de la orden (trd-core#1190). */
  pickupCode?: string | null;
  observations?: { code: string; detail: string }[];
  /** Si la venta pidió factura (§2.4). */
  invoice?: InvoiceEmissionResult;
  error?: { code?: string; message?: string };
}

/** Por qué el drenador se pausa: la sesión, el negocio o el usuario. */
export type PausaDelDrenador = "sesion" | "suspendido" | "sin-permiso";

export interface DesenlaceDelLote {
  desenlaces: PosDesenlace[];
  /** Se pausa: es una condición del negocio o de la sesión, no de la venta. */
  pausa?: PausaDelDrenador;
  /** Lo que el lote entero dice, para la pantalla de pendientes. */
  aviso?: string;
}

export type EnvioDelLote =
  | { ok: true; cuerpo: unknown }
  | { ok: false; error: unknown };

function textoDelError(error: unknown): string {
  const estado = estadoDelError(error);
  const codigo = codigoDelError(error);
  const data = (error as { response?: { data?: any } })?.response?.data;
  const mensaje =
    data?.message ?? data?.data?.message ?? (error as Error)?.message ?? "";
  const cabeza = codigo ?? (estado ? `HTTP ${estado}` : "Sin respuesta");
  return mensaje ? `${cabeza}: ${mensaje}` : cabeza;
}

function desenlaceDeLaVenta(
  saleId: string,
  resultado: SaleResult | undefined
): PosDesenlace {
  if (!resultado) {
    return {
      saleId,
      estado: "pendiente",
      error: "El servidor no devolvió esta venta",
    };
  }
  const error = resultado.error
    ? `${resultado.error.code ?? "ERROR"}: ${resultado.error.message ?? ""}`
    : null;
  switch (resultado.status) {
    case "recognized":
      return { saleId, estado: "reconocida", respuesta: resultado };
    case "with_observations":
      return { saleId, estado: "con_observaciones", respuesta: resultado };
    case "needs_attention":
      // Una falla de core, no de la venta: la orden y la bandeja van en la
      // misma transacción, así que reintentar es seguro. Si falla una y otra
      // vez con la misma venta, Rust la aparta (`falloDelServidor`).
      if (resultado.error?.code === "INTERNAL_SERVER_ERROR") {
        return { saleId, estado: "pendiente", error, falloDelServidor: true };
      }
      return {
        saleId,
        estado: "necesita_atencion",
        respuesta: resultado,
        error,
      };
    case "conflict":
      // `IDEMPOTENCY_KEY_REUSED`: la misma clave llegó con otro contenido. Es
      // un bug, y la venta queda a la vista.
      return {
        saleId,
        estado: "necesita_atencion",
        respuesta: resultado,
        error,
      };
    default:
      return {
        saleId,
        estado: "necesita_atencion",
        respuesta: resultado,
        error: `Estado desconocido: ${resultado.status}`,
      };
  }
}

const todas = (
  saleIds: string[],
  desenlace: Omit<PosDesenlace, "saleId">
): PosDesenlace[] => saleIds.map((saleId) => ({ saleId, ...desenlace }));

export const AVISO_LOTE_MAL_FORMADO =
  "El servidor no aceptó el lote: es un error de la caja. Las ventas quedaron para revisar.";
export const AVISO_VENTAS_DEL_FORMATO_2 =
  "El servidor no aceptó las ventas hechas con el mandato: quedaron para revisar. Las demás siguen subiendo.";

/** El formato de la venta que lleva `offline`, o que sólo entiende un core de la ronda 2. */
const FORMATO_CON_MANDATO = 2;

/**
 * El `formatVersion` de cada venta del lote, por `saleId`: lo que la cola
 * necesita para decidir qué hacer con un `400` (§1 de OFFLINE-POS-RONDA-2.md).
 */
export function formatosDelLote(
  ventas: readonly Record<string, unknown>[]
): Record<string, number> {
  const formatos: Record<string, number> = {};
  for (const venta of ventas) {
    if (
      typeof venta?.saleId === "string" &&
      typeof venta.formatVersion === "number"
    ) {
      formatos[venta.saleId] = venta.formatVersion;
    }
  }
  return formatos;
}
export const AVISO_DADA_DE_BAJA =
  "La caja está dada de baja: sus ventas quedaron para revisar. Si se reactiva, se reintentan desde acá.";
export const AVISO_NO_ES_ESTA_CAJA =
  "El servidor dice que este equipo no es esta caja: las ventas quedaron a la vista y no se borran.";

/**
 * El desenlace de cada venta del lote, según la tabla de §2.2. `formatos` es el
 * `formatVersion` de cada venta (`formatosDelLote`): con un `400`, sólo las del
 * formato 2 quedan a la vista, porque es lo que un core de la ronda 1 no
 * entiende; las del formato 1 vuelven a pendiente y siguen subiendo.
 */
export function desenlacesDelLote(
  envio: EnvioDelLote,
  saleIds: string[],
  formatos: Record<string, number> = {}
): DesenlaceDelLote {
  if (envio.ok) {
    const cuerpo = envio.cuerpo as {
      results?: unknown;
      data?: { results?: unknown };
    };
    // Core responde `{ isSuccess, data: { results } }`, y el BFF lo reenvía.
    const resultados = cuerpo?.data?.results ?? cuerpo?.results;
    if (!Array.isArray(resultados)) {
      return {
        desenlaces: todas(saleIds, {
          estado: "pendiente",
          error: "La respuesta no trajo los resultados",
        }),
      };
    }
    const porVenta = new Map(
      (resultados as SaleResult[]).map((r) => [r?.saleId, r])
    );
    return {
      desenlaces: saleIds.map((id) => desenlaceDeLaVenta(id, porVenta.get(id))),
    };
  }

  const { error } = envio;
  const estado = estadoDelError(error);
  const codigo = codigoDelError(error);
  const texto = textoDelError(error);

  // Red, timeout, 408, 429 o 5xx: vuelve a pendiente, con backoff. Un 5xx
  // cuenta como falla del servidor: cinco seguidas con la misma venta la apartan.
  if (
    estado === undefined ||
    estado === 408 ||
    estado === 429 ||
    estado >= 500
  ) {
    return {
      desenlaces: todas(saleIds, {
        estado: "pendiente",
        error: texto,
        ...(estado !== undefined && estado >= 500
          ? { falloDelServidor: true }
          : {}),
      }),
    };
  }
  // 401, también `stale_business_state`: se pausa y se pide el refresco único.
  if (estado === 401) {
    return {
      desenlaces: todas(saleIds, {
        estado: "pendiente",
        error: texto,
        sinEspera: true,
      }),
      pausa: "sesion",
    };
  }
  // Un lote mal formado es un bug de la caja (core responde 422 a un cuerpo
  // que su validación rechaza). Pero si el lote mezcla ventas del formato 2 con
  // otras, lo más probable es que core haya vuelto a la ronda 1 y no entienda
  // el formato 2: esas quedan a la vista, y las otras siguen subiendo, sin
  // esperar (#359).
  if (estado === 400 || estado === 422) {
    const delFormato2 = saleIds.filter(
      (id) => formatos[id] === FORMATO_CON_MANDATO
    );
    if (delFormato2.length === 0) {
      return {
        desenlaces: todas(saleIds, {
          estado: "necesita_atencion",
          error: texto,
        }),
        aviso: AVISO_LOTE_MAL_FORMADO,
      };
    }
    return {
      desenlaces: saleIds.map((saleId) =>
        delFormato2.includes(saleId)
          ? { saleId, estado: "necesita_atencion", error: texto }
          : { saleId, estado: "pendiente", error: texto, sinEspera: true }
      ),
      aviso: AVISO_VENTAS_DEL_FORMATO_2,
    };
  }
  if (estado === 403) {
    switch (codigo) {
      case "TERMINAL_DEACTIVATED":
        return {
          desenlaces: todas(saleIds, {
            estado: "necesita_atencion",
            error: texto,
          }),
          aviso: AVISO_DADA_DE_BAJA,
        };
      case "TERMINAL_INSTALLATION_MISMATCH":
        return {
          desenlaces: todas(saleIds, {
            estado: "necesita_atencion",
            error: texto,
          }),
          aviso: AVISO_NO_ES_ESTA_CAJA,
        };
      case "authz_unavailable":
        // Transitorio: el BFF no pudo consultar a identity.
        return {
          desenlaces: todas(saleIds, { estado: "pendiente", error: texto }),
        };
      case "business_suspended":
        return {
          desenlaces: todas(saleIds, {
            estado: "pendiente",
            error: texto,
            sinEspera: true,
          }),
          pausa: "suspendido",
        };
      case "not_permitted":
        return {
          desenlaces: todas(saleIds, {
            estado: "pendiente",
            error: texto,
            sinEspera: true,
          }),
          pausa: "sin-permiso",
        };
    }
  }
  // Cualquier otro 403, con código o sin él, y lo que la tabla no nombra.
  return {
    desenlaces: todas(saleIds, { estado: "necesita_atencion", error: texto }),
  };
}

/**
 * Las observaciones de una venta, en palabras del mostrador. Un código que la
 * caja no conoce se muestra con su detalle.
 */
export function observacionesDe(
  respuesta: unknown
): { codigo: string; texto: string; detalle: string }[] {
  const lista = (respuesta as SaleResult | null)?.observations;
  if (!Array.isArray(lista)) return [];
  return lista.map((o) => ({
    codigo: o.code,
    texto: textoDeLaObservacion(o.code),
    detalle: o.detail,
  }));
}
